import React, { useState } from 'react'
import { View, Text, FormFieldGroup, ScreenReaderContent, Checkbox, CheckboxGroup, SimpleSelect, Alert, Button, Spinner } from '@instructure/ui'

import PropTypes from 'prop-types'

import Papa from 'papaparse'

import { toDataURI } from './utils'
import { fetchMembers } from './fetchMembers'

/**
 * JISC Surveys export panel (self-contained).
 *
 * Internal state managed here:
 * - alerts: user feedback messages
 * - roles: selected LTI roles to include
 * - format: CSV layout key ("jisc" or "jisc-name")
 * - members: fetched roster cache
 * - isLoading / isWriting: network vs. file generation progress flags
 *
 * Props kept minimal: just LTI server base and jwt for authenticated roster fetches.
 *
 * CSV formats:
 * - jisc: login_id, empty placeholder column, email
 * - jisc-name: login_id, empty placeholder, email, empty placeholder, full name
 *
 * @param {{ ltiServer: string, jwt: string }} props
 * @returns {JSX.Element}
 */
function JiscSurveysPanel({ ltiServer, jwt }) {
	// Local state: alerts, roles, format, members, loading/writing
	const [alerts, setAlerts] = useState([])
	const defaultRoles = ['http://purl.imsglobal.org/vocab/lis/v2/membership#Learner']
	const [roles, setRoles] = useState(defaultRoles)
	const [format, setFormat] = useState('jisc')
	const [members, setMembers] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isWriting, setIsWriting] = useState(false)

	const options = [
		{ value: 'jisc', name: 'Jisc Online Surveys (without names)' },
		{ value: 'jisc-name', name: 'Jisc Online Surveys (with names)' }
	]

	/**
	 * Handle format selection changes from SimpleSelect.
	 * @param {Event} e
	 * @param {{ value: string }} param1
	 */
	const handleSelect = (e, { value }) => setFormat(value)
	/**
	 * Map member objects to CSV row arrays based on current format.
	 * @param {Array<Object>} membersArg
	 * @returns {Array<Array<string>>}
	 */
	const membersToRows = (membersArg) => {
		switch (format) {
			case 'jisc':
				return membersArg.map(member => [member.login_id, '', member.email])
			case 'jisc-name':
				return membersArg.map(member => [member.login_id, '', member.email, '', member.name])
		}
	}

	/**
	 * Generate CSV string (async wrapper to keep interface uniform).
	 * Filters members by selected roles, excludes unactivated placeholders,
	 * and raises a warning alert if some users skipped.
	 * @param {Array<Object>} membersListArg
	 * @returns {Promise<string>}
	 */
	const csvContent = (membersListArg) => {
		const allowedRoles = roles
		const list = membersListArg ?? members ?? []
		const roleMembers = list.filter(
			member => member.roles.map(
				role => allowedRoles.includes(role)
			).includes(true)
		)

		const activeMembers = roleMembers.filter(user => user.login_id !== "$Canvas.user.loginId")
		const unactivated = roleMembers.length - activeMembers.length
		if (unactivated > 0) {
			setAlerts(current => current.concat([{
				variant: 'warning',
				message: `${unactivated} users weren't included because they have not yet activated their accounts yet`
			}]))
		}

		const rows = membersToRows(activeMembers)
		return new Promise(resolve => resolve(Papa.unparse(rows)))
	}

	/**
	 * Build CSV and trigger browser download.
	 * @param {Array<Object>} membersListArg
	 */
	const sendCsv = (membersListArg) => {
		setIsWriting(true)
		csvContent(membersListArg).then(csvContentStr => {
			const encodedUri = encodeURI(toDataURI(csvContentStr))
			const link = document.createElement('a')
			link.setAttribute('href', encodedUri)
			link.setAttribute('download', 'members.csv')
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
		}).finally(() => setIsWriting(false))
	}

	/**
	 * Orchestrate conditional fetch + download.
	 */
	const download = () => {
		if (members == null) {
			setIsLoading(true)
			fetchMembers(ltiServer, jwt)
				.then(membersResult => {
					setMembers(membersResult)
					sendCsv(membersResult)
				})
				.catch(reason => {
					setAlerts(current => current.concat([{ variant: 'error', message: 'Failed to load members: ' + reason.message }]))
				})
				.finally(() => setIsLoading(false))
		} else {
			sendCsv(members)
		}
	}
	/**
	 * Render alert messages.
	 * @returns {JSX.Element[]}
	 */
	const renderAlerts = (list) => list && list.map((alert, index) => (
		<Alert margin="small" key={index} variant={alert.variant} renderCloseButtonLabel="Close">{alert.message}</Alert>
	))

	const isProcessing = isLoading || isWriting
	return (
		<View as="div" padding="small">
			{renderAlerts(alerts)}
			<View as="div" padding="medium none">
				<Text color="primary">
					This allows you to export a list of course members in a CSV format that can be directly imported into a JISC Online survey.
				</Text>
			</View>

			<FormFieldGroup description={<ScreenReaderContent>Limit Result</ScreenReaderContent>}>
				<CheckboxGroup
					name="roles"
					onChange={(value) => setRoles(value)}
					defaultValue={defaultRoles}
					description="Members to export"
				>
					<Checkbox label="Staff" value="http://purl.imsglobal.org/vocab/lis/v2/membership#Instructor" />
					<Checkbox label="Learners" value="http://purl.imsglobal.org/vocab/lis/v2/membership#Learner" />
				</CheckboxGroup>
				<SimpleSelect
					renderLabel="CSV Format"
					assistiveText="Use arrow keys to navigate options."
					value={format}
					onChange={handleSelect}
				>
					{options.map((opt, index) => (
						<SimpleSelect.Option key={index} id={`opt-${index}`} value={opt.value}>
							{opt.name}
						</SimpleSelect.Option>
					))}
				</SimpleSelect>
				<Alert key="delete-after-download" variant="info">
					Please ensure you delete the file once you have used it for importing survey participants into JISC Online surveys.
				</Alert>
						<View as="div">
							<Button onClick={download} padding="small" color="primary" interaction={isProcessing ? 'disabled' : 'enabled'}>
						Download
					</Button>
					{isProcessing && <Spinner size="x-small" margin="0 medium" renderTitle="Loading course members" />}
				</View>
			</FormFieldGroup>
		</View>
	)
}

JiscSurveysPanel.propTypes = {
	ltiServer: PropTypes.string.isRequired,
	jwt: PropTypes.string.isRequired
}

export default JiscSurveysPanel
